import {
  FormSelect,
  Heading,
} from "@/components";
import { Card, CardContent, Tabs, TabsContent, TabsList, TabsTrigger, } from "@/components/ui";
import { FormProvider } from "react-hook-form";
import { actionItems, DOCUMENT_TYPE, tabs } from "./consts";
import useCreateJEManual from "./useCreateJEManual";
import { cn } from "@/lib/utils";
import { ManualEntry, DownloadTemplate, UploadTemplate } from "./Common";
import { PaymentDownloadTemplate, PaymentManualEntry } from "./Payment";

const CreateJEManual = () => {
  const {
    breadcrumbData,
    breadcrumbTitle,
    currentFinanceYear,
    headerFieldCount,
    lineItemFieldCount,
    form,
    requiredFields,
    isListLoading,
    documentTypeList,
    currentConfig,
    onTabValueChange,
    activeTab,
    configFields,
    isRefetchingConfigFields,
    isMobile,
    isVp
  } = useCreateJEManual();
  const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6 !pt-4">
      <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between mb-6">
        <div className="flex flex-row items-center justify-between">
          <Heading title={breadcrumbTitle} breadcrumbData={breadcrumbData} />
        </div>
        <div className="flex w-full flex-col items-stretch gap-2 md:w-auto md:flex-row md:flex-wrap md:items-center">
          <span className="text-xl md:text-2xl font-semibold text-primary">
            {currentFinanceYear}
          </span>
          <div className="w-full min-w-[189px] max-w-full rounded-md border border-g-600 bg-white px-4 py-2 text-g-600 shadow-sm md:w-auto">
            <div className="text-[12px] leading-5 text-g-400">
              Document Type
            </div>
            <div className="text-[14px] font-medium leading-5 text-g-500">
              {currentConfig?.documentTypeDisplayName || ""}
            </div>
          </div>
          <div className="w-full min-w-[151px] max-w-full rounded-md border border-g-200 bg-white px-4 py-2 shadow-sm md:w-auto">
            <div className="text-[11px] leading-5 text-g-400">
              Template Code
            </div>
            <div className="text-[14px] font-medium leading-5 text-g-500">
              {currentConfig?.documentType || ""}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="gap-0 rounded-md py-4">
          <CardContent className="px-4">
            <div className="text-sm font-semibold text-primary">
              Document Type Selection
            </div>
            <p className="mt-2 text-[12px] leading-5 text-g-400 md:mt-3">
              User selects the document type first. The system resolves the
              configured header, detail, upload template, and business
              validations.
            </p>
            <div className="mt-3 md:mt-4">
              <FormProvider {...form}>
                <FormSelect
                  name={DOCUMENT_TYPE}
                  control={form.control}
                  label="Document Type"
                  placeholder="Please select"
                  isRequired={requiredFields.includes(DOCUMENT_TYPE)}
                  error={errors?.[DOCUMENT_TYPE]?.message as string}
                  options={documentTypeList?.filter((data: any) => data?.documentType != "OC")?.map((data: any) => ({
                    label: `${data?.documentType} - ${data?.documentTypeDisplayName}`,
                    value: data?.documentTypeId?.toString(),
                  }))}
                  disabled={isListLoading}
                />
              </FormProvider>
            </div>

            <div className="mt-5 rounded-lg border border-blue-100 bg-blue-50 p-4">
              <div className="text-[14px] font-semibold leading-6 text-primary">
                Resolved Configuration
              </div>
              <p className="mt-3 text-[12px] leading-5 text-g-500">
                Post a direct journal entry using configured header and line
                item fields.
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <div className="flex w-[150px] max-w-full items-center justify-between rounded-full bg-blue-100 px-4 py-2 text-[12px] text-g-500">
                  <span>Header Fields:</span>
                  <span className="text-[12px] text-g-500">
                    {String(headerFieldCount).padStart(2, "0")}
                  </span>
                </div>
                <div className="flex w-[150px] max-w-full items-center justify-between rounded-full bg-blue-100 px-4 py-2 text-[12px] text-g-500">
                  <span>Line Items:</span>
                  <span className="text-[12px] text-g-500">
                    {String(lineItemFieldCount).padStart(2, "0")}
                  </span>
                </div>
                <div className="flex w-[150px] max-w-full items-center justify-between rounded-full bg-blue-100 px-4 py-2 text-[12px] text-g-500">
                  <span>Prefix:</span>
                  <span className="text-[12px] text-g-500">
                    {currentConfig?.documentType || ""}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-5 rounded-[10px] border border-g-200 p-4">
              <div className="text-sm font-semibold text-primary">
                Available Actions:
              </div>
              <div className="mt-4 flex flex-col gap-4">
                {actionItems.map((action: any) => {
                  const Icon = action.icon;
                  return (
                    <div
                      key={action.title}
                      className="flex gap-3 rounded-[14px] border border-dashed border-g-200 p-4"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-primary">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-g-500">
                          {action.title}
                        </div>
                        <p className="mt-2 text-[12px] leading-5 text-g-400">
                          {action.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="lg:col-span-3">
          <Tabs
              value={activeTab}
              onValueChange={onTabValueChange}
            >
              <TabsList className={cn("gap-x-4", isMobile ? "!w-full !overflow-x-scroll" : "")}>
                {tabs.map((tab: any) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="data-[state=active]:shadow-none hover:cursor-pointer text-gray-400 px-0 border-0 shadow-none data-[state=active]:rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:text-black"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="manual">
                {isVp ? <PaymentManualEntry configFields={configFields} isLoading={isRefetchingConfigFields} /> : <ManualEntry configFields={configFields} isLoading={isRefetchingConfigFields} />}
              </TabsContent>
              <TabsContent value="download">
                {isVp ? <PaymentDownloadTemplate currentConfig={currentConfig} configFields={configFields} isLoading={isRefetchingConfigFields} /> : <DownloadTemplate currentConfig={currentConfig} configFields={configFields} isLoading={isRefetchingConfigFields} />}
              </TabsContent>
              <TabsContent value="upload">
                <UploadTemplate currentConfig={currentConfig} isLoading={isRefetchingConfigFields} />
              </TabsContent>
            </Tabs>
        </div>
      </div>
    </div>
  );
};

export default CreateJEManual;

